import { type CSSProperties } from 'react';
import { Save, X, AlertCircle } from 'lucide-react';
import { useStore } from '../store';

const input: CSSProperties = { width: '100%', fontSize: 13, padding: '8px 10px', borderRadius: 6, border: '1px solid #E2E2E6', boxSizing: 'border-box', fontFamily: 'inherit' };
const label: CSSProperties = { fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#75757F', marginBottom: 5, display: 'block' };

/** Create/edit-organisation modal. Renders nothing unless a draft is open in the store. */
export function CampaignEditor() {
  const draft = useStore((s) => s.campaignModal);
  const saving = useStore((s) => s.campaignSaving);
  const error = useStore((s) => s.campaignError);
  const set = useStore((s) => s.setCampaignField);
  const close = useStore((s) => s.closeCampaignModal);
  const save = useStore((s) => s.saveCampaign);
  if (!draft) return null;

  return (
    <div role="dialog" aria-modal="true" aria-label={draft.mode === 'create' ? 'New organisation' : 'Edit organisation'}
      onClick={close}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 60, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '8vh 16px' }}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-card" style={{ width: '100%', maxWidth: 560, padding: 24, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
          <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>{draft.mode === 'create' ? 'New organisation' : 'Edit organisation'}</h2>
          <button type="button" aria-label="Close" onClick={close} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#75757F' }}><X size={20} /></button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={label} htmlFor="org-name">Name</label>
            <input id="org-name" style={input} value={draft.name} placeholder="e.g. Q3 Capability Uplift" onChange={(e) => set('name', e.target.value)} />
          </div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ flex: '2 1 200px' }}>
              <label style={label} htmlFor="org-sector">Sector</label>
              <input id="org-sector" style={input} value={draft.sector} placeholder="e.g. Financial services" onChange={(e) => set('sector', e.target.value)} />
            </div>
            <div style={{ flex: '1 1 130px' }}>
              <label style={label} htmlFor="org-status">Status</label>
              <select id="org-status" style={input} value={draft.status} onChange={(e) => set('status', e.target.value)}>
                <option value="draft">Draft</option>
                <option value="active">Active</option>
                <option value="closed">Closed</option>
                <option value="archived">Archived</option>
              </select>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 150px' }}>
              <label style={label} htmlFor="org-start">Period start</label>
              <input id="org-start" type="date" style={input} value={draft.periodStart} onChange={(e) => set('periodStart', e.target.value)} />
            </div>
            <div style={{ flex: '1 1 150px' }}>
              <label style={label} htmlFor="org-end">Period end</label>
              <input id="org-end" type="date" style={input} value={draft.periodEnd} onChange={(e) => set('periodEnd', e.target.value)} />
            </div>
            <div style={{ flex: '1 1 130px' }}>
              <label style={label} htmlFor="org-budget">Budget (USD)</label>
              <input id="org-budget" type="number" min={0} style={input} value={draft.budgetUsd} onChange={(e) => set('budgetUsd', e.target.value)} />
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'flex-end', marginTop: 22 }}>
          {error && <span style={{ fontSize: 12.5, color: '#C0392B', display: 'inline-flex', alignItems: 'center', gap: 6, marginRight: 'auto' }}><AlertCircle size={14} /> {error}</span>}
          <button type="button" onClick={close} className="border border-panel text-storm" style={{ background: 'transparent', fontSize: 13, fontWeight: 600, borderRadius: 6, padding: '9px 14px', cursor: 'pointer' }}>Cancel</button>
          <button type="button" onClick={save} disabled={saving} className="bg-code text-white" style={{ border: 'none', fontSize: 13, fontWeight: 700, borderRadius: 6, padding: '9px 16px', cursor: saving ? 'wait' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 7 }}>
            <Save size={14} /> {saving ? 'Saving…' : draft.mode === 'create' ? 'Create' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
